import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Card,
  Button,
  Input,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Tabs,
  Tag,
  Badge,
  Select,
  Modal,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  FileTextOutlined,
  PhoneOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { AdminPermissions, StaffPermissions } from "../../../model/auth/Permissions";
import { useMember } from "../../../common/member/MemberContext";
import { useMemberService } from "../../../services/members/MemberServiceProvider";
import { useMemberFollowupService } from "../../../services/members/MemberFollowupServiceProvider";
import { useStaffUserService } from "../../../services/members/StaffUserServiceProvider";
import { useLeadOwnershipFilter } from "../../../common/crm/useLeadOwnershipFilter";
import { useCallsService } from "../../../services/members/CallsServiceProvider";
import { Call, CallOutcome } from "../../../model/members/Calls";
import { MemberConversion } from "../../../model/members/MemberConversion";
import { useLoading } from "../../../common/loading/LoadingContext";
import { useClient } from "../../../common/ClientProvider";
import { useTokens } from "../../../core/theme/TokensProvider";
import { contrastTextOn } from "../../../core/theme/buildTokens";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  GridReadyEvent,
  GridApi,
  RowClassParams,
  GetContextMenuItemsParams,
  SelectionChangedEvent,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { MemberProfile } from "../../../model/members/MemberProfile";
import { MemberFollowup } from "../../../model/members/MemberFollowup";
import { StaffUser } from "../../../model/members/StaffUser";
import { LeadAssignmentNote, LeadAssignmentState } from "../../../model/members/StaffTask";
import { RadioGroup } from "../../../ui/primitives/Radio";
import { Button as PrimitiveButton } from "../../../ui/primitives/Button";
import { UpdateMemberRequest } from "../../../requests/member/UpdateMemberRequest";
import {
  buildMembersWithPrimaryFollowup,
  byNextDueDate,
  classifyRetentionTab,
  RetentionTab,
  nextDueDate,
} from "../../../utils/memberFollowupUtils";
import EditFollowupModal from "./EditFollowupModal";
import LeadDetailPanel from "./LeadDetailPanel";
import ConversionsModal from "./ConversionsModal";
import {
  LEAD_SOURCE_COLORS,
  LEAD_TYPE_COLORS,
  LeadRow,
  interestsLabel,
  leadSourceLabel,
  leadTypeLabel,
} from "./leadDisplay";

const { Search } = Input;
const { Title } = Typography;

// The lead's current handler. Read-only by design: the handler is whoever is logged in and
// acting on the lead (the API stamps it server-side), so a manual dropdown here would be a
// second, conflicting way to set it. Bulk reassignment lives in "Assign calls".
const ManagedByCellRenderer = (params: any) => {
  const member: MemberProfile | undefined = params.data?.member;
  if (!member) return null;
  return <span data-testid={`retention-managed-by-${member.membershipId}`}>{member.managedByStaffName ?? "—"}</span>;
};

/**
 * Keeps a click on a cell's own control from also reaching the row.
 *
 * React's `stopPropagation` cannot do this: AG Grid binds a native listener on the row
 * element, so its handler — which both dispatches `rowClicked` and toggles selection —
 * runs before React sees the event at all. AG Grid's own escape hatch is a flag it checks
 * on the native event, so set that.
 */
const stopRowHandling = (e: React.MouseEvent) => {
  (e.nativeEvent as any).__ag_Grid_Stop_Propagation = true;
};

// FORT-322: per-lead staff-note affordance (reuses member.metadata.Notes). The button
// is highlighted when a note exists — the column's job is to show *whether* a lead has
// been written on, at a glance down the list. Clicking opens the lead, where the note
// itself is read and edited.
const NoteCellRenderer = (params: any) => {
  const row: LeadRow | undefined = params.data;
  const ctx = params.context ?? {};
  const member = row?.member;
  if (!row || !member) return null;
  const note = member.metadata?.Notes;
  const hasNote = typeof note === "string" && note.trim().length > 0;
  // While assigning, the row belongs to the tick — a button that opened a panel over the
  // list you're picking from would fight the mode it sits in. Read off cellRendererParams
  // rather than context: columnDefs already rebuild when the mode flips, so this re-renders
  // with it, whereas a context change alone doesn't refresh cells.
  const disabled = Boolean(params.selectionMode);
  return (
    <Button
      size="small"
      type={hasNote ? "primary" : "default"}
      icon={<FileTextOutlined />}
      disabled={disabled}
      title={hasNote ? "Has a staff note" : "Add a staff note"}
      aria-label={hasNote ? `Staff note for ${member.name}` : `Add a staff note for ${member.name}`}
      onClick={(e) => {
        stopRowHandling(e);
        ctx.onOpenLead?.(row);
      }}
      data-testid={`retention-note-${member.membershipId}`}
    />
  );
};

// The lead's email, as a live mailto rather than text to copy out by hand. Stops the row
// click so opening the detail panel and starting an email stay separate actions.
//
// Laid out so the address truncates and the link doesn't: an address long enough to fill
// the column would otherwise push the one interactive thing in the cell out of sight.
const EmailCellRenderer = (params: any) => {
  const member: MemberProfile | undefined = params.data?.member;
  const email = member?.emailAddress?.trim();
  if (!email) return null;
  return (
    <span
      style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}
      data-testid={`retention-email-${member?.membershipId}`}
    >
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={email}>
        {email}
      </span>
      <a
        href={`mailto:${encodeURIComponent(email)}`}
        title={`Email ${member?.name ?? "lead"}`}
        aria-label={`Email ${member?.name ?? "lead"}`}
        style={{ flexShrink: 0 }}
        onClick={stopRowHandling}
      >
        <MailOutlined />
      </a>
    </span>
  );
};

// One list, used for both the tab strip and the per-tab outstanding counts.
const TAB_DEFS: { key: RetentionTab; label: string }[] = [
  { key: "visitors", label: "Visitors" },
  { key: "tours", label: "Tours" },
  { key: "calls", label: "Calls" },
  { key: "current", label: "Current Members" },
  { key: "previous", label: "Previous Members" },
  { key: "legacy", label: "Legacy Members" },
  { key: "other", label: "Other" },
  { key: "deadleads", label: "Dead Leads" },
];

const MemberRetentionScreen: React.FC = () => {
  const memberService = useMemberService();
  const followupService = useMemberFollowupService();
  const staffUserService = useStaffUserService();
  const callsService = useCallsService();
  const { permissions } = useMember();
  const { withLoading, startLoading, stopLoading } = useLoading();
  const { currentClientId } = useClient();
  const tokens = useTokens();
  const navigate = useNavigate();
  // The selection bar is painted with the tenant's sider colour, so its text has to be
  // chosen against that rather than assumed dark.
  const selectionBarText = useMemo(() => contrastTextOn(tokens.color.sidebar.bg), [tokens]);

  const [members, setMembers] = useState<MemberProfile[]>([]);
  const [followups, setFollowups] = useState<MemberFollowup[]>([]);
  const [staffUsers, setStaffUsers] = useState<StaffUser[]>([]);
  const [searchText, setSearchText] = useState("");
  const [searchParams] = useSearchParams();
  // FORT-376: the landing filter and the toggle, shared with the Business Development CRM.
  // See useLeadOwnershipFilter for the tier rule and why a defaulted choice never writes the
  // URL. showMineOnly is null until the tier and staff identity settle — the leads read below
  // waits on that so it fires once, with the right scope.
  const { showMineOnly, chooseFilter } = useLeadOwnershipFilter();

  // Assigning work is manager tier (staff:tasks:create). Without this the control renders
  // for Staff and fails with a 403 only after they've picked an assignee and typed a note.
  //
  // The legacy members:manage-followups is deliberately absent: /dashboard/tasks/assign-calls
  // is gated on CanCreateStaffTasks, which doesn't honour it, so accepting it here would put
  // the button in front of every reception account for the same 403-at-submit reason.
  const canAssignWork = useMemo(
    () =>
      (permissions?.permissions ?? []).some(
        (p) => p.name === StaffPermissions.CreateTasks || p.name === AdminPermissions.FortiusAdmin
      ),
    [permissions]
  );
  const [activeTab, setActiveTab] = useState<string>("visitors");
  
  const gridApiRef = useRef<GridApi | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalRow, setEditModalRow] = useState<LeadRow | null>(null);
  // The lead whose detail panel is open, and the draft of its staff note.
  const [detailRow, setDetailRow] = useState<LeadRow | null>(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  // Selection mode: tick boxes and the selection bar appear only while assigning, and a
  // row click ticks a lead rather than opening it.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMemberships, setSelectedMemberships] = useState<string[]>([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignStaffId, setAssignStaffId] = useState<number | undefined>(undefined);
  const [assignNote, setAssignNote] = useState<string>("");
  const [assignmentNotes, setAssignmentNotes] = useState<LeadAssignmentNote[]>([]);
  // Whether each of MY assigned leads is still outstanding or already dealt with today —
  // what drives the blue / completed row colours.
  const [assignmentStates, setAssignmentStates] = useState<LeadAssignmentState[]>([]);
  // FORT-361: my open calls, keyed by lead. The colour state above says *that* a lead is
  // outstanding; this carries the task id needed to log an outcome against it.
  const [myCalls, setMyCalls] = useState<Call[]>([]);
  // Which outcome is mid-flight, so the panel spins only the button that was pressed.
  const [pendingOutcome, setPendingOutcome] = useState<CallOutcome | null>(null);
  // FORT-360: the conversion counter and the list behind it.
  const [conversions, setConversions] = useState<MemberConversion[]>([]);
  const [conversionCount, setConversionCount] = useState(0);
  const [conversionsOpen, setConversionsOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [memberProfiles, followupList, staffUserList, assignmentNotes, assignmentStates, calls] =
        await withLoading(
        Promise.all([
          // Every lead in the tenant; showMineOnly narrows it server-side to this user's.
          memberService.getRetentionLeads(showMineOnly === true),
          followupService.getFollowups(),
          // Staff is a secondary source for the "Spoke To" dropdown; a failure
          // here must not blank the whole retention screen, so it degrades to
          // an empty list rather than rejecting the combined load.
          staffUserService.getStaffUsers().catch(() => [] as StaffUser[]),
          // Read separately rather than joined onto the leads query: that query is a shared
          // multi-map used well beyond this screen. A failure here must not blank the CRM.
          memberService.getLeadAssignmentNotes().catch(() => [] as LeadAssignmentNote[]),
          // Same reasoning: degrades to no colouring rather than blanking the CRM.
          memberService.getMyCallAssignmentStates().catch(() => [] as LeadAssignmentState[]),
          // My open calls — the task ids behind the outcome buttons in the lead panel.
          // Degrades to none, which just hides those buttons (FORT-361).
          callsService.getMyCalls(dayjs().format("YYYY-MM-DD")).catch(() => [] as Call[]),
        ])
      );
      setMembers(memberProfiles);
      setFollowups(followupList);
      setStaffUsers(staffUserList);
      setAssignmentNotes(assignmentNotes);
      setAssignmentStates(assignmentStates);
      setMyCalls(calls);
    } catch (error) {
      console.error("Error fetching retention data:", error);
      message.error("Failed to fetch members and follow-ups");
    }
  }, [memberService, followupService, staffUserService, callsService, withLoading, showMineOnly]);

  useEffect(() => {
    // Not before the landing filter is decided — otherwise the leads read fires once with the
    // wrong scope and again with the right one, and the grid visibly rewrites itself.
    //
    // Held under the global spinner rather than simply skipped: withLoading is what raises it,
    // so without this the screen would sit in its resolved-but-empty state — zero tab counts,
    // empty grid, and the tenant-wide conversion counter already populated — for the length of
    // the permissions + /dashboard/me round trip. On a slow link that reads as "no leads".
    if (showMineOnly === null) {
      // startLoading/stopLoading rather than withLoading: the latter takes a promise, and the
      // only promise available here is one that never settles — which would leak the spinner
      // and the promise. This raises it and the cleanup lowers it when the decision lands.
      startLoading();
      return () => stopLoading();
    }
    fetchData();
  }, [fetchData, showMineOnly, startLoading, stopLoading]);

  // FORT-360: conversions are tenant-wide, so they load once rather than riding on
  // fetchData — that re-runs on every My Leads toggle, and re-running a whole-table
  // aggregate to answer a question the filter cannot change is waste. Degrades to zero:
  // the counter is a badge on the pipeline, not the pipeline.
  useEffect(() => {
    let cancelled = false;
    memberService
      .getConversions()
      .then((summary) => {
        if (cancelled) return;
        setConversionCount(summary.totalCount);
        setConversions(summary.conversions);
      })
      .catch(() => {
        if (cancelled) return;
        setConversionCount(0);
        setConversions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [memberService]);

  const handleSaveFollowup = useCallback(
    async (memberId: string, followup: MemberFollowup) => {
      const clientName = currentClientId ?? "";
      const followupWithClient = {
        ...followup,
        clientName: followup.clientName ?? clientName,
      };
      await followupService.saveFollowup(memberId, followupWithClient, clientName);
    },
    [followupService, currentClientId]
  );

  const handleSaveMemberMetadata = useCallback(
    async (member: MemberProfile, metadata: Record<string, any>) => {
      const updateRequest: UpdateMemberRequest = {
        name: member.name,
        username: member.username,
        dateOfBirth: member.dateOfBirth,
        emailAddress: member.emailAddress,
        addressLine1: member.addressLine1,
        addressLine2: member.addressLine2,
        townCity: member.townCity,
        countyState: member.countyState,
        postZipCode: member.postZipCode,
        phoneNumber: member.phoneNumber,
        membershipId: member.membershipId,
        specialOffers: member.specialOffers,
        membershipCategory: member.membershipCategory,
        metadata,
        isEnabled: member.isEnabled,
        validFrom: dayjs(member.validFrom).format('YYYY-MM-DD'),
        validTo: dayjs(member.validTo).format('YYYY-MM-DD'),
        clientName: currentClientId,
        auth0Id: member.auth0Id,
        status: member.status,
        country: member.country,
        changeType: 'Update',
      };
      await memberService.update(updateRequest);
    },
    [memberService, currentClientId]
  );

  // Opening a lead loads its staff note into the panel's editor.
  const openLead = useCallback((row: LeadRow) => {
    setDetailRow(row);
    const note = row.member.metadata?.Notes;
    setNotesDraft(typeof note === "string" ? note : "");
  }, []);

  // FORT-322: per-lead staff note (member.metadata.Notes), reusing the member-update path.
  const saveNotes = useCallback(async () => {
    const notesMember = detailRow?.member;
    if (!notesMember) return;
    const nextMetadata = { ...(notesMember.metadata ?? {}), Notes: notesDraft };
    setSavingNote(true);
    try {
      await handleSaveMemberMetadata(notesMember, nextMetadata);
      setMembers((prev) =>
        prev.map((m) =>
          m.membershipId === notesMember.membershipId ? { ...m, metadata: nextMetadata } : m
        )
      );
      // Acting on a lead makes you its handler (item 11). Server-side resolves who that is
      // from the caller — best-effort, since the note itself is already saved.
      try {
        await memberService.claimLead(notesMember.membershipId);
      } catch {
        // Non-fatal: the note saved; the handler stamp is a side effect. The call passes
        // showError:false so this can't contradict the "Note saved" message below.
      }
      message.success("Note saved");
      setDetailRow(null);
      await fetchData();
    } catch (error) {
      console.error("Error saving staff note:", error);
      message.error("Failed to save note");
    } finally {
      setSavingNote(false);
    }
  }, [detailRow, notesDraft, handleSaveMemberMetadata, memberService, fetchData]);

  // FORT-361: my open call on the lead currently open in the panel, if any. Keyed by
  // membership id because that is what both the calls queue and the CRM rows carry.
  const openCallByMembershipId = useMemo(() => {
    const map = new Map<string, number>();
    myCalls.forEach((c) => {
      if (c.membershipId) map.set(c.membershipId, c.taskId);
    });
    return map;
  }, [myCalls]);

  // Logging an outcome writes a follow-up attributed to me and completes the call, so the
  // row turns white and the count drains — the same end state as saving a note, reached by
  // one click for the common "no answer" case.
  const handleRecordOutcome = useCallback(
    async (taskId: number, outcome: CallOutcome, note: string, nextFollowUpDate: string) => {
      setPendingOutcome(outcome);
      try {
        await callsService.recordOutcome(taskId, {
          outcome,
          note: note.trim() || null,
          nextFollowUpDate,
        });
        message.success("Call outcome recorded");
        setDetailRow(null);
        await fetchData();
      } catch (error) {
        console.error("Error recording call outcome:", error);
        message.error("Failed to record outcome");
      } finally {
        setPendingOutcome(null);
      }
    },
    [callsService, fetchData]
  );

  // Leaving selection mode always clears the selection: a tick the user can no longer see
  // would otherwise still be counted the next time they opened the assign panel.
  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    gridApiRef.current?.deselectAll();
    setSelectedMemberships([]);
  }, []);

  // FORT-323: bulk "Assign calls" — track the checkbox selection and hand the batch
  // to a staff member as calls to make.
  const onSelectionChanged = useCallback((event: SelectionChangedEvent<LeadRow>) => {
    const rows = event.api.getSelectedRows();
    setSelectedMemberships(rows.map((r) => r.member.membershipId));
  }, []);

  const handleAssignCalls = useCallback(async () => {
    if (assignStaffId == null || selectedMemberships.length === 0) return;
    try {
      const assigned = await memberService.assignCalls(assignStaffId, selectedMemberships, assignNote);
      const staffName = staffUsers.find((s) => s.id === assignStaffId)?.name ?? "staff";
      // "Assigned" counts leads handed over, not call tasks created — re-assigning a lead
      // that already has an open call still moves it, and that's what the manager did.
      message.success(`Assigned ${assigned} lead${assigned === 1 ? "" : "s"} to ${staffName}`);
      setAssignModalOpen(false);
      setAssignStaffId(undefined);
      setAssignNote("");
      // The assignment is done, so selection mode has served its purpose — leave it, which
      // also clears the ticks so the "(N)" count can't go stale. Then refresh so any newly
      // created calls are reflected.
      exitSelectionMode();
      await fetchData();
    } catch (error) {
      console.error("Error assigning calls:", error);
      message.error("Failed to assign calls");
    }
  }, [assignStaffId, assignNote, selectedMemberships, memberService, staffUsers, fetchData, exitSelectionMode]);

  const membersWithFollowups = useMemo(() => {
    const noteByMembershipId = new Map(assignmentNotes.map((n) => [n.membershipId, n.note]));
    return buildMembersWithPrimaryFollowup(members, followups, {
      includeMembersWithNoFollowup: true,
    }).map((row) => ({
      ...row,
      assignmentNote: noteByMembershipId.get(row.member.membershipId),
    }));
  }, [members, followups, assignmentNotes]);

  const filteredRows = useMemo(() => {
    let rows = membersWithFollowups;

    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();
      rows = rows.filter(
        (r) =>
          r.member.name?.toLowerCase().includes(q) ||
          r.member.emailAddress?.toLowerCase().includes(q) ||
          r.member.phoneNumber?.toLowerCase().includes(q)
      );
    }

    // Tab membership is driven by classifyRetentionTab, which segments by
    // LeadSource but reroutes converted prospects (LeadType === "Membership")
    // out of the Visitors/Tours tabs into Current/Previous by active status.
    rows = rows.filter((r) => classifyRetentionTab(r.member) === activeTab);

    // Soonest-due first. This used to be a default sort on the Next Follow-Up column,
    // which now lives in the detail panel — applying it to the rows keeps the page in the
    // order it has always been in rather than letting a column move reshuffle it.
    return [...rows].sort(byNextDueDate);
  }, [
    membersWithFollowups,
    searchText,
    activeTab,
  ]);

  const summaryStats = useMemo(() => {
    const today = dayjs().startOf("day");
    const endOfWeek = today.add(7, "day");
    let dueToday = 0;
    let overdue = 0;
    let thisWeek = 0;
    for (const r of filteredRows) {
      const nfd = nextDueDate(r);
      if (!nfd) continue;
      const d = dayjs(nfd).startOf("day");
      if (d.isSame(today)) dueToday++;
      else if (d.isBefore(today)) overdue++;
      else if (d.isSameOrBefore(endOfWeek)) thisWeek++;
    }
    return {
      total: filteredRows.length,
      dueToday,
      overdue,
      thisWeek,
    };
  }, [filteredRows]);

  // Read through a ref inside the cell renderer: columnDefs is memoised on its own deps, and
  // rebuilding every column whenever an assignment changes would reset AG Grid's column state.
  const assignmentStateRef = useRef<Map<string, string>>(new Map());

  const stateByMembershipId = useMemo(
    () => new Map(assignmentStates.map((a) => [a.membershipId, a.state])),
    [assignmentStates]
  );
  assignmentStateRef.current = stateByMembershipId;

  // Outstanding assignments per tab, computed over ALL rows rather than the visible tab —
  // that is the whole point: it tells the user where the rest of the day's work is.
  const outstandingByTab = useMemo(() => {
    const counts: Partial<Record<RetentionTab, number>> = {};
    membersWithFollowups.forEach((row) => {
      if (stateByMembershipId.get(row.member.membershipId) !== "Outstanding") return;
      const tab = classifyRetentionTab(row.member);
      counts[tab] = (counts[tab] ?? 0) + 1;
    });
    return counts;
  }, [membersWithFollowups, stateByMembershipId]);

  // Arriving from Staff Daily's "N calls to make", open the tab that actually holds the work.
  // The default tab is Visitors, and a call can be assigned to a lead anywhere in the
  // pipeline, so landing on an empty Visitors tab would contradict the number just clicked.
  // Runs once, and only when the deep link asked for it — after that the tab is the user's.
  //
  // FORT-376: keyed on the ARRIVAL parameter, not on showMineOnly. Once My Leads became a
  // landing default, testing the filter value would have jumped the tab for every reception
  // user on every visit — a behaviour change well outside a default-selection ticket, and one
  // that would have made this comment untrue.
  const deepLinkTabPicked = useRef(false);
  const arrivedViaMineDeepLink = useRef(searchParams.get("mine") === "1");
  useEffect(() => {
    if (deepLinkTabPicked.current || !arrivedViaMineDeepLink.current) return;
    const entries = Object.entries(outstandingByTab) as [RetentionTab, number][];
    if (entries.length === 0) return;
    deepLinkTabPicked.current = true;
    const [busiest] = entries.sort((a, b) => b[1] - a[1]);
    setActiveTab(busiest[0]);
  }, [outstandingByTab]);

  const getRowClass = useCallback(
    (params: RowClassParams<LeadRow>) => {
      const row = params.data;
      if (!row) return "";

      // A lead assigned to ME is coloured by whether I've dealt with it, and that wins over
      // the due-date tints: this is the day's work list, and "have I done this one" is the
      // question being asked of it. Outstanding work is blue; dealt-with work renders plain
      // white (FORT-359 — the client reads white as "done", so the old green tint is gone;
      // the ✓ name marker still says why the row is uncoloured). Returning "" rather than
      // falling through matters: a dealt-with lead must not pick the overdue/due-today tint
      // back up, or it would read as still needing work.
      const assignment = stateByMembershipId.get(row.member.membershipId);
      if (assignment === "Outstanding") return "ag-row-assigned-outstanding";
      if (assignment === "Completed") return "";

      const nfd = nextDueDate(row);
      if (!nfd) return "";
      const d = dayjs(nfd).startOf("day");
      const today = dayjs().startOf("day");
      if (d.isBefore(today)) return "ag-row-followup-overdue";
      if (d.isSame(today)) return "ag-row-followup-due-today";
      return "";
    },
    [stateByMembershipId]
  );

  // The eight columns you scan a pipeline by. Everything else about a lead — how they
  // heard, who referred them, the follow-up history, the call note — is in the detail
  // panel: fifteen columns didn't fit, so AG Grid virtualised half of them off-screen and
  // what was left read as a wall of cramped cells.
  const columnDefs = useMemo<ColDef<LeadRow>[]>(
    () => [
      // Tick boxes appear only once you're assigning. Outside selection mode a row click
      // opens the lead, so a permanent checkbox column would be a control most visits
      // never use sitting in front of the one they do.
      ...(selectionMode
        ? [
            {
              headerName: "",
              colId: "select",
              width: 44,
              pinned: "left" as const,
              sortable: false,
              filter: false,
              resizable: false,
              checkboxSelection: true,
              headerCheckboxSelection: true,
              headerCheckboxSelectionFilteredOnly: true,
            },
          ]
        : []),
      {
        headerName: "Name",
        field: "member.name",
        // Assignment state gets a text marker as well as the row colour: colour alone is
        // invisible to a colour-blind user, and three tints already compete for that channel.
        cellRenderer: (params: any) => {
          const state = params.data
            ? assignmentStateRef.current.get(params.data.member.membershipId)
            : undefined;
          const marker =
            state === "Outstanding" ? "● " : state === "Completed" ? "✓ " : "";
          return (
            <span title={state === "Outstanding" ? "Assigned to you — still to do"
              : state === "Completed" ? "Assigned to you — done today" : undefined}>
              {marker}
              {params.value}
            </span>
          );
        },
        // The row tint and the KPI counts key off the next follow-up date, which is now in
        // the detail panel — so keep it one hover away rather than leaving the colours
        // unexplained.
        tooltipValueGetter: (params: any) => {
          const due = params.data ? nextDueDate(params.data) : null;
          if (!due) return "No follow-up scheduled";
          const d = dayjs(due);
          return d.isValid() ? `Next follow-up ${d.format("DD/MM/YYYY")}` : "No follow-up scheduled";
        },
        sortable: true,
        filter: "agTextColumnFilter",
        flex: 1,
        minWidth: 130,
      },
      {
        headerName: "Email",
        field: "member.emailAddress",
        cellRenderer: EmailCellRenderer,
        sortable: true,
        filter: "agTextColumnFilter",
        flex: 1.4,
        minWidth: 170,
      },
      {
        headerName: "Phone",
        field: "member.phoneNumber",
        sortable: true,
        filter: "agTextColumnFilter",
        width: 125,
      },
      {
        headerName: "Lead Type",
        colId: "LeadType",
        valueGetter: (params) => (params.data ? leadTypeLabel(params.data.member) : ""),
        sortable: true,
        filter: true,
        width: 130,
        cellRenderer: (params: any) => {
          const val = params.value;
          if (!val) return null;
          const color = LEAD_TYPE_COLORS[val] || "default";
          return <Tag color={color}>{val}</Tag>;
        },
      },
      {
        headerName: "Source",
        colId: "LeadSource",
        valueGetter: (params) => (params.data ? leadSourceLabel(params.data.member) : ""),
        sortable: true,
        filter: true,
        width: 110,
        cellRenderer: (params: any) => {
          const val = params.value;
          if (!val) return null;
          const color = LEAD_SOURCE_COLORS[val] || "default";
          return <Tag color={color}>{val}</Tag>;
        },
      },
      {
        headerName: "Interests",
        colId: "Interests",
        valueGetter: (params) => (params.data ? interestsLabel(params.data.member) : ""),
        sortable: true,
        filter: "agTextColumnFilter",
        width: 130,
      },
      {
        headerName: "Managed by",
        colId: "Managed by",
        valueGetter: (params) => params.data?.member?.managedByStaffName ?? "",
        cellRenderer: ManagedByCellRenderer,
        sortable: true,
        filter: "agTextColumnFilter",
        width: 120,
      },
      {
        headerName: "Notes",
        colId: "StaffNote",
        cellRenderer: NoteCellRenderer,
        cellRendererParams: { selectionMode },
        sortable: false,
        filter: false,
        width: 80,
      },
    ],
    [selectionMode]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
    }),
    []
  );

  const onGridReady = useCallback((params: GridReadyEvent<LeadRow>) => {
    gridApiRef.current = params.api;
  }, []);

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<LeadRow>) => {
      const row = params.node?.data;
      if (!row?.member?.membershipId) return [];
      return [
        {
          name: "Go to Profile",
          action: () => {
            navigate(`/profiles/${row.member.membershipId}`);
          },
        },
      ];
    },
    [navigate]
  );

  // Sorting Dead Leads to the bottom
  const onSortChanged = (params: any) => {
    // Custom post-sort could be done here if needed, but we can do it via the grid's postSort feature
  };
  
  const postSortRows = useCallback((params: any) => {
    const rowNodes = params.nodes;
    let nextInsertPos = 0;
    for (let i = 0; i < rowNodes.length; i++) {
      const leadType = rowNodes[i].data?.member?.metadata?.LeadType;
      if (leadType === "Dead Lead") {
        rowNodes.push(rowNodes.splice(i, 1)[0]);
        i--;
        if (nextInsertPos++ > rowNodes.length) break; // prevent infinite loops
      }
    }
  }, []);

  return (
    <div style={{ padding: "24px", display: "flex", flexDirection: "column", height: "100vh", boxSizing: "border-box" }}>
      {/*
        FORT-360: the title row carries the conversion counter on the right. The title was a
        bare element before, so it is wrapped rather than restyled — the counter belongs
        beside the screen's name, not buried in the KPI strip with the follow-up numbers,
        which measure work outstanding rather than work that paid off.
      */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          margin: "0 0 16px",
        }}
      >
        <Title level={2} style={{ margin: 0 }}>SALES CRM – MEMBER RETENTION</Title>
        <PrimitiveButton
          variant="default"
          onClick={() => setConversionsOpen(true)}
          data-testid="retention-conversions-counter"
        >
          {conversionCount} {conversionCount === 1 ? "conversion" : "conversions"}
        </PrimitiveButton>
      </div>

      <Row gutter={16} style={{ marginBottom: 16, flexShrink: 0 }}>
        <Col span={6}>
          <Card>
            <Statistic title={`Total in ${
              { visitors: "Visitors", tours: "Tours", calls: "Calls", current: "Current Members", previous: "Previous Members", legacy: "Legacy Members", other: "Other", deadleads: "Dead Leads" }[activeTab] ?? activeTab
            }`} value={summaryStats.total} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Due Today"
              value={summaryStats.dueToday}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Overdue"
              value={summaryStats.overdue}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="This Week" value={summaryStats.thisWeek} />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16, flexShrink: 0 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <Search
              placeholder="Search by name, email, or phone"
              allowClear
              enterButton={<SearchOutlined />}
              size="middle"
              style={{ maxWidth: 400 }}
              onSearch={(v) => setSearchText(v)}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                fetchData();
              }}
            >
              Refresh
            </Button>
          </Col>
          <Col>
            {/*
              Both options are shown, rather than one button whose label flips. As a single
              button the label said what you were LOOKING at while reading as what clicking
              would DO — so "All Leads" invited a click that filtered them away, and the only
              thing distinguishing state from action was the primary/default styling.

              buttonStyle="solid" for the same reason: the selected option has to read at a
              glance, and a subtle tint would leave the same ambiguity in a quieter form.
            */}
            <RadioGroup
              optionType="button"
              buttonStyle="solid"
              value={showMineOnly ? "mine" : "all"}
              onChange={(value) => chooseFilter(value === "mine")}
              aria-label="Lead ownership filter"
              data-testid="retention-my-leads-toggle"
              options={[
                { label: "All Leads", value: "all" },
                { label: "My Leads", value: "mine" },
              ]}
            />
          </Col>
          {canAssignWork && !selectionMode && (
          <Col>
            <Button
              type="primary"
              icon={<PhoneOutlined />}
              onClick={() => setSelectionMode(true)}
              data-testid="retention-assign-calls-btn"
            >
              Assign calls
            </Button>
          </Col>
          )}
        </Row>
      </Card>

      {/* The selection bar replaces the toolbar's assign button while you're picking leads,
          so there's always exactly one thing to press next and an obvious way back out. */}
      {selectionMode ? (
        <Card
          style={{ marginBottom: 16, flexShrink: 0, background: tokens.color.sidebar.bg }}
          styles={{ body: { padding: "10px 16px" } }}
          data-testid="retention-selection-bar"
        >
          <Row align="middle" justify="space-between" gutter={[8, 8]}>
            <Col>
              {/* Derived, not hardcoded white: the bar is painted with the tenant's sider
                  colour, and a club with a pale brand would otherwise get white-on-white. */}
              <span style={{ color: selectionBarText, fontSize: tokens.fontSize.sm }}>
                Selection mode —{" "}
                {selectedMemberships.length === 0
                  ? "tick leads to assign"
                  : `${selectedMemberships.length} selected`}
              </span>
            </Col>
            <Col>
              <Button
                onClick={exitSelectionMode}
                style={{ marginRight: 8 }}
                data-testid="retention-selection-cancel"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<PhoneOutlined />}
                disabled={selectedMemberships.length === 0}
                onClick={() => setAssignModalOpen(true)}
                data-testid="retention-assign-selected-btn"
              >
                Assign selected
                {selectedMemberships.length > 0 ? ` (${selectedMemberships.length})` : ""}
              </Button>
            </Col>
          </Row>
        </Card>
      ) : null}

      <Card style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }} styles={{ body: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ flexShrink: 0 }}
          items={TAB_DEFS.map((t) => ({
            key: t.key,
            // Badged with this user's outstanding calls on that tab. Calls get assigned to
            // leads anywhere in the pipeline, so without this the day's work is invisible on
            // every tab except the one that happens to be open (FORT-354).
            label: outstandingByTab[t.key] ? (
              <Badge
                count={outstandingByTab[t.key]}
                offset={[10, -2]}
                style={{ backgroundColor: "#1677ff" }}
              >
                <span data-testid={`retention-tab-${t.key}`}>{t.label}</span>
              </Badge>
            ) : (
              <span data-testid={`retention-tab-${t.key}`}>{t.label}</span>
            ),
          }))}
        />

        <div
          style={{ marginBottom: 8, color: "#666", fontSize: 12, flexShrink: 0 }}
          data-testid="retention-table-hint"
        >
          {selectionMode
            ? "Tick the leads to assign, then choose who makes the calls."
            : "Click a row to open the lead. Right-click for the member profile."}
        </div>
        <div className="ag-theme-alpine" style={{ flex: 1, minHeight: 0, width: "100%" }}>
          <style>{`
            .ag-row-followup-overdue { background-color: #fff2f0 !important; }
            .ag-row-followup-due-today { background-color: #fffbe6 !important; }
            /* FORT-354/FORT-359: my assigned leads — blue while outstanding, plain white once
               dealt with (no tint class; the ✓ name marker carries the done state).
               Colour alone can't carry this (the overdue/due-today tints already compete for
               the same channel, and it's invisible to a colour-blind user), so the Name column
               also renders a text marker.
               Raw hex rather than tokens: the palette has no subtle/tint variants yet, and the
               two neighbouring row tints above are literals for the same reason. Adding a tint
               scale is a design-system change, not this ticket's. */
            .ag-row-assigned-outstanding { background-color: #e6f4ff !important; }
          `}</style>
          <AgGridReact<LeadRow>
            rowData={filteredRows}
            columnDefs={columnDefs}
            context={{ onOpenLead: openLead }}
            defaultColDef={defaultColDef}
            getRowClass={getRowClass}
            onGridReady={onGridReady}
            rowSelection={selectionMode ? "multiple" : undefined}
            // In selection mode the row click is the tick — that's the mode's whole point.
            // Outside it, a click opens the lead instead.
            suppressRowClickSelection={!selectionMode}
            // Without this AG Grid treats a plain click as "select only this one", so
            // ticking a second lead would silently untick the first — and a ticked row
            // couldn't be unticked by clicking it again. Ctrl-click is not a thing anyone
            // should have to discover to assign two calls.
            rowMultiSelectWithClick={selectionMode}
            onSelectionChanged={onSelectionChanged}
            getContextMenuItems={getContextMenuItems}
            onRowClicked={(event) => {
              if (selectionMode || !event.data) return;
              // A click on the row's own controls — the mail link, the note button — is that
              // control's, not the row's. React's stopPropagation can't help here: AG Grid
              // listens natively on the row, so its handler runs before React sees the
              // event at all.
              const target = event.event?.target as HTMLElement | null;
              if (target?.closest("a, button")) return;
              openLead(event.data);
            }}
            onSortChanged={onSortChanged}
            postSortRows={postSortRows}
            animateRows
          />
        </div>
      </Card>

      <ConversionsModal
        open={conversionsOpen}
        onClose={() => setConversionsOpen(false)}
        conversions={conversions}
        totalCount={conversionCount}
      />

      <EditFollowupModal
        open={editModalOpen}
        row={editModalRow}
        onClose={() => {
          setEditModalOpen(false);
          setEditModalRow(null);
        }}
        onSaved={fetchData}
        onSaveFollowup={handleSaveFollowup}
        onSaveMember={handleSaveMemberMetadata}
        staffUsers={staffUsers}
      />

      <LeadDetailPanel
        row={detailRow}
        noteDraft={notesDraft}
        onNoteDraftChange={setNotesDraft}
        onSaveNote={saveNotes}
        savingNote={savingNote}
        openCallTaskId={
          detailRow ? openCallByMembershipId.get(detailRow.member.membershipId) ?? null : null
        }
        onRecordOutcome={handleRecordOutcome}
        pendingOutcome={pendingOutcome}
        onEditFollowup={() => {
          if (!detailRow) return;
          setEditModalRow(detailRow);
          setEditModalOpen(true);
          setDetailRow(null);
        }}
        onGoToProfile={() => {
          if (detailRow) navigate(`/profiles/${detailRow.member.membershipId}`);
        }}
        onClose={() => setDetailRow(null)}
      />

      <Modal
        open={assignModalOpen}
        title={`Assign ${selectedMemberships.length} call${selectedMemberships.length === 1 ? "" : "s"}`}
        onOk={handleAssignCalls}
        onCancel={() => setAssignModalOpen(false)}
        okText="Assign calls"
        okButtonProps={{ disabled: assignStaffId == null, "data-testid": "retention-assign-confirm" } as any}
        data-testid="retention-assign-modal"
      >
        <div style={{ marginBottom: 8 }}>
          Assign the selected leads to a staff member as calls to make today.
        </div>
        <Select
          style={{ width: "100%" }}
          placeholder="Select staff member"
          value={assignStaffId}
          options={staffUsers.map((s) => ({ label: s.name, value: s.id }))}
          onChange={(value?: number) => setAssignStaffId(value)}
          data-testid="retention-assign-staff-select"
        />
        <div style={{ marginTop: 12 }}>
          <Input.TextArea
            rows={3}
            placeholder="Note for these calls (optional) — shown against the leads in their CRM"
            value={assignNote}
            onChange={(e) => setAssignNote(e.target.value)}
            data-testid="retention-assign-note"
          />
        </div>
      </Modal>
    </div>
  );
};

export default MemberRetentionScreen;

